import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  ShieldCheck,
  Sparkles,
  Building2,
  Handshake,
  Users,
  Layers,
  MapPin,
  Star,
  Hammer,
  ClipboardCheck,
  CalendarCheck,
} from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { SERVICES, BUSINESS } from "@/lib/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1642976975710-1d8890dbf5ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgzMzg5MzE2fDA&ixlib=rb-4.1.0&q=85";

const WHY = [
  {
    icon: Building2,
    title: "Commercial Cleaning Specialists",
    body: "Commercial cleaning isn't just one of our services — it's what we do best. Our systems, equipment and quality assurance are built specifically for commercial environments.",
  },
  {
    icon: Layers,
    title: "Scalable Solutions",
    body: "From a small office to a large multi-site contract, a retail store to a builders' final clean — we have the experience and capacity to deliver projects of every size.",
  },
  {
    icon: Sparkles,
    title: "Quality Without Compromise",
    body: "Every project is completed with meticulous attention to detail, professional accountability, and a commitment to exceeding expectations.",
  },
  {
    icon: Handshake,
    title: "Reliable Long-Term Partnerships",
    body: "Trust, communication and consistency. We aim to become the preferred commercial cleaning provider for businesses, builders and property managers throughout SEQ.",
  },
  {
    icon: ShieldCheck,
    title: "Safety & Professionalism",
    body: "Our team operates with professionalism, respect, and a strong commitment to workplace safety — every project completed efficiently and to the highest standard.",
  },
];

const STEPS = [
  {
    icon: ClipboardCheck,
    title: "01 · Scope & Quote",
    body: "Send us the details of your project or contract. We assess the scope on-site or remotely and return a clear, itemised quote.",
  },
  {
    icon: CalendarCheck,
    title: "02 · Schedule the Work",
    body: "We confirm a firm start date, timing and inclusions in writing — plus a nominated point of contact for the duration of the job.",
  },
  {
    icon: Handshake,
    title: "03 · Deliver & Report",
    body: "Our team delivers to spec, walks the site before we leave, and provides sign-off documentation where required.",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Our team handled the builders' final clean before a 24-lot handover. Every unit was ready on time and to spec.",
    author: "Placeholder — Developer",
    place: "Gold Coast",
  },
  {
    quote:
      "We contract Savage Cleaning for our medical centre. The consistency and professionalism has been excellent.",
    author: "Placeholder — Practice Manager",
    place: "Brisbane",
  },
  {
    quote:
      "Our bond clean was thorough and stress-free. The property was handed back in perfect condition.",
    author: "Placeholder — Property Manager",
    place: "South East QLD",
  },
];

const FAQ = [
  {
    q: "Where do you provide commercial cleaning services?",
    a: "We service Brisbane, the Gold Coast and South East Queensland as our core regions. Sunshine Coast projects are considered by quotation. If you're not sure whether we cover your area, ask us — we can usually help.",
  },
  {
    q: "What kinds of commercial contracts do you take on?",
    a: "Offices, corporate facilities, medical centres and hospitals, childcare and schools, leisure and aquatic centres, retail and shopping centres, industrial and warehouse facilities, body corporate common areas, government buildings, and builders' final cleans for developments of any size.",
  },
  {
    q: "Do you handle builders' final cleans before handover?",
    a: "Yes — final cleans on newly completed homes, apartment complexes and commercial developments is a core speciality. We prepare properties to a handover-ready standard for settlement, client walkthrough or first occupancy.",
  },
  {
    q: "Do you still take residential cleaning work?",
    a: "Yes, but selectively. Commercial cleaning is our primary focus. We take on residential clients who value professional workmanship and reliability — and we bring the same commercial-grade standard to every home we clean.",
  },
  {
    q: "How do we start working together?",
    a: "Send an enquiry via the contact form or call 0416 238 430. We'll ask the right questions upfront so the quote is clear and accurate, and can arrange a site inspection where useful.",
  },
];

const GALLERY_PREVIEW = [
  "https://images.unsplash.com/photo-1627141440602-e9c1e5fd2fbf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBob21lJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzgzMzg5MzE2fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1603712725038-e9334ae8f39f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjbGVhbmVyJTIwd29ya2luZ3xlbnwwfHx8fDE3ODMzODkzMTZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjbGVhbmVyJTIwd29ya2luZ3xlbnwwfHx8fDE3ODMzODkzMTZ8MA&ixlib=rb-4.1.0&q=85",
];

export default function Home() {
  return (
    <>
      <SEO
        title="Savage Cleaning — Commercial Cleaning Solutions | Brisbane, Gold Coast & SEQ"
        description="Savage Cleaning is a trusted commercial cleaning provider across Brisbane, the Gold Coast and South East Queensland — specialising in builders' final cleans, commercial contracts and bond cleans. Request a free quote."
      />

      {/* BRAND BANNER — full uncropped banner asset */}
      <section
        className="relative bg-[#0a0a0b] border-b border-[#C9A24B]/20"
        data-testid="brand-banner"
      >
        <div className="max-w-[1400px] mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_savage-clean/artifacts/cep0hv12_A3w37.webp"
            alt="Savage Cleaning — Commercial and Domestic Cleaning · 0416 238 430 · info@savagecleaning.com"
            className="block w-full h-auto"
            data-testid="brand-banner-image"
          />
        </div>
      </section>

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Commercial building interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0B]/95 via-[#0A0A0B]/70 to-[#0A0A0B]/95" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-28 sm:py-40 lg:py-48">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3">
              <span className="gold-rule" />
              <span className="eyebrow">Brisbane · Gold Coast · SEQ</span>
            </div>
            <h1
              className="mt-6 font-serif-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05]"
              data-testid="hero-headline"
            >
              Commercial Cleaning Solutions.
              <br />
              <span className="text-[#C9A24B] italic">Delivered without compromise.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-white/85 leading-relaxed max-w-3xl">
              Professional commercial cleaning solutions across Brisbane, the Gold Coast and South
              East Queensland — for businesses, builders, developers, healthcare facilities,
              education providers, property managers and commercial property owners.
            </p>
            <p className="mt-4 text-base sm:text-lg text-white/70 leading-relaxed max-w-3xl">
              We specialise in builders' final cleans, commercial cleaning contracts and
              end-of-lease bond cleans, while also offering selected residential cleaning services.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-quote">
                Get a Free Quote <ArrowRight size={16} />
              </Link>
              <a href="tel:0416238430" className="btn-outline" data-testid="hero-cta-call">
                <Phone size={16} /> Call 0416 238 430
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 sm:py-32 relative" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">About Savage Cleaning</span>
            <h2 className="mt-6 font-serif-display text-4xl sm:text-5xl text-white leading-tight">
              Commercial cleaning solutions you can depend on.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-white/80 text-lg leading-relaxed">
            <p>
              Savage Cleaning is a trusted commercial cleaning solutions provider, delivering
              premium cleaning services for businesses, builders, developers, healthcare
              facilities, education providers, property managers and commercial property owners.
            </p>
            <p>
              From builders' final cleans before handover to ongoing commercial maintenance
              contracts, our experienced team is equipped to deliver projects of every size with
              the same uncompromising commitment to quality.
            </p>
            <p>
              Alongside our commercial expertise, we also provide professional end-of-lease bond
              cleans and selected residential cleaning services for clients who expect the same
              exceptional standards.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-[#C9A24B] uppercase text-sm tracking-[0.2em] mt-4"
              data-testid="intro-about-link"
            >
              Read our story <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section
        className="py-24 sm:py-32 border-t border-white/5 bg-[#111114]"
        data-testid="services-overview"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="eyebrow">Our services</span>
              <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl text-white leading-tight max-w-xl">
                Commercial-led. Broad in capability.
              </h2>
            </div>
            <Link
              to="/services"
              className="btn-outline self-start md:self-end"
              data-testid="services-overview-cta"
            >
              View all services <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s, idx) => (
              <Link
                to="/services"
                key={s.slug}
                className={`card-dark group ${s.featured ? "border-[#C9A24B]/50" : ""}`}
                data-testid={`service-card-${s.slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[#C9A24B] text-xs tracking-[0.25em] uppercase">
                    0{idx + 1}
                  </span>
                  {s.featured && (
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B] border border-[#C9A24B]/60 px-2 py-1">
                      Core focus
                    </span>
                  )}
                </div>
                <h3 className="mt-4 font-serif-display text-2xl text-white group-hover:text-[#C9A24B] transition-colors">
                  {s.title}
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed">{s.short}</p>
                <div className="mt-6 inline-flex items-center gap-2 text-[#C9A24B] text-xs tracking-[0.25em] uppercase">
                  Explore <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 sm:py-32" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="eyebrow">Why Savage Cleaning</span>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl text-white leading-tight">
              Built for commercial. Trusted long-term.
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              We're structured to be the preferred commercial cleaning partner for businesses,
              builders and property managers throughout South East Queensland.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="card-dark"
                data-testid={`why-item-${title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon size={30} strokeWidth={1.4} className="text-[#C9A24B]" />
                <h3 className="mt-5 font-serif-display text-2xl text-white">{title}</h3>
                <p className="mt-3 text-white/70 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="py-24 sm:py-32 bg-[#111114] border-y border-white/5"
        data-testid="process-section"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">How we work</span>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl text-white leading-tight">
              A simple, professional process.
            </h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="relative pt-8">
                <div className="absolute top-0 left-0 h-px w-16 bg-[#C9A24B]" />
                <Icon size={30} strokeWidth={1.4} className="text-[#C9A24B]" />
                <h3 className="mt-5 font-serif-display text-2xl text-white">{title}</h3>
                <p className="mt-3 text-white/70 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AREAS WE SERVICE */}
      <section className="py-24 sm:py-32" data-testid="areas-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow">Areas we service</span>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl text-white leading-tight">
              Proudly servicing South East Queensland.
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              Based on the Gold Coast, we service commercial and residential clients across the
              region — from Brisbane's CBD through to the Gold Coast and by quotation into the
              Sunshine Coast.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {BUSINESS.serviceAreas.map((area) => (
              <div
                key={area}
                className="border border-white/10 hover:border-[#C9A24B]/50 transition-colors px-6 py-5 flex items-center gap-4"
                data-testid={`area-item-${area.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`}
              >
                <MapPin size={20} className="text-[#C9A24B]" strokeWidth={1.5} />
                <span className="text-white text-lg">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-24 sm:py-32 bg-[#111114] border-y border-white/5"
        data-testid="faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <span className="eyebrow">FAQ</span>
            <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl text-white leading-tight">
              Answers before you ask.
            </h2>
          </div>
          <div className="mt-12">
            {FAQ.map((f, i) => (
              <details key={i} className="faq-item" data-testid={`faq-item-${i}`}>
                <summary>
                  <span>{f.q}</span>
                  <span className="plus">+</span>
                </summary>
                <div className="answer">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Ready for a commercial cleaner you can rely on?"
        subtitle="Whether you need a one-off project or a long-term cleaning partner, we deliver exceptional results with professionalism, reliability and meticulous attention to detail."
      />
    </>
  );
}
